import { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { useVideoScanSummary } from "../../hooks/useVideoLogs"
import { aggregateFeedback, answerTones } from "../../utils/feedbackAggregate"

// Two fills one row of the comment grid; the rest go behind the disclosure.
const COMMENTS_VISIBLE = 2

const QuestionRow = ({ label, answers }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-gray-600">{label}</span>
    <div className="flex flex-wrap justify-end gap-1">
      {answers.map((answer) => (
        <span
          key={answer.key}
          className={`rounded-full border px-2 py-0.5 text-[11px] ${answerTones[answer.tone]}`}
        >
          {answer.label} · {answer.count}
        </span>
      ))}
    </div>
  </div>
)

const CommentGrid = ({ comments }) => (
  <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
    {comments.map((comment) => (
      <div key={comment.id} className="rounded-lg bg-white px-3 py-2.5">
        <p className="whitespace-pre-wrap text-xs italic leading-relaxed text-gray-700">
          {comment.text}
        </p>
        <p className="mt-1 text-right text-[11px] text-gray-400">
          — response {comment.responseNumber}
        </p>
      </div>
    ))}
  </div>
)

/*
  Every feedback response for one report, collapsed into answer counts.

  A card per response reads fine for one and badly for six, and what an operator
  looks for here is the shape of the answers rather than who said what. The
  individual responses are still listed one by one in the QR Scan Analytics
  drawer, which keeps the per-response card untouched.

  Responses are anonymous: a comment is attributed only by its position in the
  response list, which is the same number the drawer prints.
*/
const FeedbackSummaryCard = ({ uuid }) => {
  const { data } = useVideoScanSummary(uuid)
  const feedback = data?.feedback

  const aggregate = useMemo(() => aggregateFeedback(feedback || []), [feedback])

  const visibleComments = aggregate.comments.slice(0, COMMENTS_VISIBLE)
  const hiddenComments = aggregate.comments.slice(COMMENTS_VISIBLE)

  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Feedback responses
        </p>
        {aggregate.total > 0 && (
          <span className="rounded-md bg-white px-2 py-0.5 text-[11px] text-gray-600">
            {aggregate.total} response{aggregate.total === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {aggregate.total === 0 ? (
        <p className="text-xs italic text-gray-400">
          No feedback submitted for this report.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 lg:grid-cols-2">
            {aggregate.questions.map((question) => (
              <QuestionRow
                key={question.key}
                answers={question.answers}
                label={question.label}
              />
            ))}
            <QuestionRow
              answers={aggregate.delivery.answers}
              label={aggregate.delivery.label}
            />
          </div>

          {aggregate.comments.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-3">
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Comments · {aggregate.comments.length}
              </p>

              <CommentGrid comments={visibleComments} />

              {hiddenComments.length > 0 && (
                <details className="group mt-2.5">
                  {/* The label stays the same open and closed: swapping it to
                      "Hide" announces a different name than the one just
                      activated. */}
                  <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded text-xs text-blue-700 [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary">
                    <FontAwesomeIcon
                      className="h-3 w-3 transition-transform group-open:rotate-180"
                      icon={faChevronDown}
                    />
                    Show {hiddenComments.length} more comment
                    {hiddenComments.length === 1 ? "" : "s"}
                  </summary>
                  <div className="mt-2.5">
                    <CommentGrid comments={hiddenComments} />
                  </div>
                </details>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default FeedbackSummaryCard
